// @ts-nocheck
import {
  Button,
  ButtonGroup,
  DialogActionTrigger,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { useForm, type SubmitHandler } from "react-hook-form"
import { FaExchangeAlt } from "react-icons/fa"

import {
  type ApiError,
  type FeedMetadataPublic,
  type FeedUpdate,
  ReportsService,
} from "@/client"
import useCustomToast from "@/hooks/useCustomToast"
import { handleError } from "@/utils"
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog"
import { Field } from "../ui/field"

interface EditFeedProps {
  feed: FeedUpdate & { id: string }
}

const EditFeed = ({ feed }: EditFeedProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FeedUpdate>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      feed_name: feed.feed_name,
      feed_description: feed.feed_description,
      feed_url: feed.feed_url,
    },
  })

  const mutation = useMutation({
    mutationFn: (data: FeedUpdate) =>
      ReportsService.updateFeed({ id: feed.id, requestBody: data }),
    onSuccess: () => {
      showSuccessToast("피드가 성공적으로 수정되었습니다.")
      reset()
      setIsOpen(false)
      setHasSubmitted(false)
      queryClient.invalidateQueries({ queryKey: ["feedMetadata"] })
    },
    onError: (err: ApiError) => {
      handleError(err)
      setHasSubmitted(false)
    },
  })

  const onSubmit: SubmitHandler<FeedUpdate> = (data) => {
    if (hasSubmitted) return
    setHasSubmitted(true)
    mutation.mutate(data)
  }

  return (
    <DialogRoot
      size={{ base: "xs", md: "md" }}
      placement="center"
      open={isOpen}
      onOpenChange={({ open }) => {
        setIsOpen(open)
        if (open) {
          reset({
            feed_name: feed.feed_name,
            feed_description: feed.feed_description,
            feed_url: feed.feed_url,
          })
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="ghost">
          <FaExchangeAlt fontSize="16px" />
          피드 수정
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>피드 수정</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Text mb={4}>아래 피드 세부 정보를 업데이트하세요.</Text>
            <VStack gap={4}>
              <Field
                required
                invalid={!!errors.feed_name}
                errorText={errors.feed_name?.message}
                label="이름"
              >
                <Input
                  id="feed_name"
                  {...register("feed_name", {
                    required: "피드 이름을 입력해주세요.",
                  })}
                  placeholder="피드 이름"
                />
              </Field>

              <Field
                required
                invalid={!!errors.feed_description}
                errorText={errors.feed_description?.message}
                label="설명"
              >
                <Input
                  id="feed_description"
                  {...register("feed_description", {
                    required: "피드 설명을 입력해주세요.",
                  })}
                  placeholder="피드 설명"
                />
              </Field>

              <Field
                required
                invalid={!!errors.feed_url}
                errorText={errors.feed_url?.message}
                label="RSS 피드 URL"
              >
                <Input
                  id="feed_url"
                  {...register("feed_url", {
                    required: "피드 URL을 입력해주세요.",
                  })}
                  placeholder="https://example.com/rss"
                />
              </Field>
            </VStack>
          </DialogBody>

          <DialogFooter gap={2}>
            <ButtonGroup>
              <DialogActionTrigger asChild>
                <Button variant="subtle" colorPalette="gray" disabled={isSubmitting}>
                  취소
                </Button>
              </DialogActionTrigger>
              <Button
                variant="solid"
                type="submit"
                disabled={isSubmitting || hasSubmitted}
                isLoading={isSubmitting}
              >
                저장
              </Button>
            </ButtonGroup>
          </DialogFooter>
        </form>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  )
}

export default EditFeed
